import { gql } from "@/gql/__generated__";
import {
  AppApiKeysQuery,
  SubscriptionAppApiKeysQuery
} from "@/gql/__generated__/graphql";
import { useMutation } from "@apollo/client";
import { AppOrigin } from "../../features/organization-apps/types/app.type";
import { useUserActiveOrganization } from "../user/useUserActiveOrganization";
import { APP_API_KEYS, SUBSCRIPTION_APP_API_KEYS } from "./appAPIKeys";

const APP_API_KEY_REVOKE = gql(`
  mutation appAPIKeyRevoke($id: ID!) {
    appAPIKeyRevoke(id: $id) {
      __typename
      id
      secret
    }
  }
`);

interface Props {
  apiKeyID: string;
  appSlug: string | undefined;
  subscriptionId: string | undefined;
  origin: AppOrigin | undefined;
}

export function useAppAPIKeyRevoke({
  apiKeyID,
  appSlug,
  subscriptionId,
  origin
}: Props) {
  const { activeOrganization } = useUserActiveOrganization();

  return useMutation(APP_API_KEY_REVOKE, {
    variables: { id: apiKeyID },
    update(cache, { data }) {
      if (!data || !activeOrganization) return;

      const revokedAPIKey = data.appAPIKeyRevoke;

      if (origin === "organization" && appSlug)
        cache.updateQuery(
          {
            query: APP_API_KEYS,
            variables: { organizationSlug: activeOrganization.slug, appSlug }
          },
          (cacheData) => {
            if (!cacheData?.app?.defaultDeployment) return cacheData;

            const updatedAppAPIKeys =
              cacheData.app.defaultDeployment.apiKeys.filter(
                (key) => key?.id !== revokedAPIKey.id
              );

            const updatedApp: AppApiKeysQuery = {
              __typename: "Query",
              app: {
                ...cacheData.app,
                defaultDeployment: {
                  ...cacheData.app.defaultDeployment,
                  apiKeys: updatedAppAPIKeys
                }
              }
            };

            return updatedApp;
          }
        );

      if (origin === "subscription" && subscriptionId)
        cache.updateQuery(
          {
            query: SUBSCRIPTION_APP_API_KEYS,
            variables: { subscriptionId }
          },
          (cacheData) => {
            if (!cacheData) return cacheData;

            const updatedAppAPIKeys = cacheData.subscription.apiKeys.filter(
              (key) => key?.id !== revokedAPIKey.id
            );

            const updatedApp: SubscriptionAppApiKeysQuery = {
              __typename: "Query",
              subscription: {
                ...cacheData.subscription,
                apiKeys: updatedAppAPIKeys
              }
            };

            return updatedApp;
          }
        );
    }
  });
}
